// create-dialog.ts — the Create-release modal's DOM (owner batch 2026-09-05,
// docs/create-release-browsers.md; extracted from home.ts so the suite can
// drive the REAL dialog without mounting the whole grid page).
//
// Decisions live in create-release.ts (pure, drilled by home.test.mjs);
// this module owns only the DOM. The display contract this batch ships:
//
//   D3  — no verdict text under the fields: each field carries an in-field
//         icon seat (.hc-vicon, check/X) whose entrance and exit are
//         registered animations; the verdict SENTENCE rides data-tip +
//         aria-label + a visually-hidden aria-live line per field.
//   D1  — the Create button is disabled until every field is green
//         (createSubmitOk); the POST still re-judges at submit.
//   D2  — Name availability: debounced (never per-keystroke) probe of
//         GET /api/releases/name-available, stale answers dropped by seq.
//   D4  — the modal's box never changes size: every variable seat is a
//         fixed-height reservation (CSS), the template row toggles
//         visibility (NEVER hidden/display), and nothing in the form flow
//         flips [hidden] after build — the suite probes exactly that.
//   D5  — the config-template picker is the console's mkDropdown (full
//         width), mirrored into a hidden input so the POST is unchanged.
//   D6  — the source-branch typeahead over the modal-open branch cache
//         (uncapped /api/vcs/branch-names; load measured by
//         typeaheadBootLine on the console). The suggestion pop is an
//         absolute OVERLAY — opening it moves no layout.
//
// DOM discipline: createElement/textContent/classList throughout (the
// dialog.ts precedent) so the stub-document harness drives this module
// without a browser; the only innerHTML writes are icon() sprite refs.

import { openDialog, showDialogMsg } from './dialog.js';
import { mkIconBtn, setBtnState } from './buttons.js';
import { icon } from './generated/icons.js';
import { mkDropdown, type DropdownCtl } from './dropdown.js';
import { attachFcTypeahead, mkFcToggle, mkFcValidityWriter } from './form-controls.js';
import { templatePickerVm } from './config-templates-vm.js';
import { ackBody, ackVm, mountPreflight, type PreflightDto } from './template-preflight.js';
import { prefersReducedMotion, MOTION_LEAVE_FALLBACK_MS } from './motion.js';
import { mountCreateBrowse, fetchAllBranches, type BranchFetchResult } from './branch-browser.js';
import {
  BRANCH_SYNTAX_REASON, CREATE_BRANCH_HINT, SHA_LATEST_PLACEHOLDER,
  branchFieldVm, branchIconVm, clientNameGate, createSubmitOk, nameFieldVm,
  prefillVm, shaFieldVm, typeaheadBootLine, typeaheadOptions,
  type CreateSeatConfig, type NameProbe, type ShaProbe, type TypeaheadVm,
  type ValidityIconVm,
} from './create-release.js';
import {
  CONVERT_TOGGLE_HINT, CONVERT_TOGGLE_LABEL, convertPanelVm, convertRequestBody,
  probeDisqualifies, probeFromFetch,
  type ConvertEpicsDto, type ConvertFetch, type ConvertPanelVm,
} from './convert-corpus.js';
import {
  CONFIGURE_BTN_LABEL, configureButtonVm, configureSummary, openConfigureModal,
  type ConfigureModalOpts,
} from './convert-configure-modal.js';

async function jsonFetch<T>(method: string, url: string, body?: unknown): Promise<{ status: number; data: T | null }> {
  try {
    const res = await fetch(url, {
      method,
      headers: body !== undefined ? { 'content-type': 'application/json' } : undefined,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    let data: T | null = null;
    try { data = (await res.json()) as T; } catch { /* tolerate non-JSON */ }
    return { status: res.status, data };
  } catch {
    return { status: 0, data: null };
  }
}

export interface CreateDialogOpts {
  /** Read at submit time (home.ts resolves it after whoami). */
  apex: () => string | null;
  /** Reload the grid/slots after a create that stays on this page. */
  onCreated: () => void | Promise<void>;
  /** Probe debounce (ms). The default is the D2 figure; suites pass 0. */
  debounceMs?: number;
}

/** The one debounce figure (D2): no request per keystroke, and a pause
 * this short still reads as real time. */
export const CREATE_PROBE_DEBOUNCE_MS = 300;

interface NameAvailablePayload {
  available?: boolean;
  reason?: string | null;
  measured?: string;
}

interface CorpusBasePayload {
  resolvedSha?: string;
  measured?: string;
  error?: string;
}

/** In-field validity icon (D3): the treatment is the SHARED writer now
 * (pi-stack/src/client/form-controls.ts, mkFcValidityWriter) — no verdict
 * prose under the field, an in-field icon whose entrance and exit are
 * registered animations, and the sentence on data-tip + aria-label + the
 * sibling live line. The `hc-vicon` prefix and the console's own glyph pair
 * are passed in, because those class names are what this stylesheet's
 * reduced-motion guard registers BY NAME.
 *
 * PR-3a extracted it because the create dialog was the only place in the
 * product a field could say WHY it was invalid without adding a line of
 * prose — a treatment worth having everywhere and reachable nowhere. */
function makeViconWriter(): (seat: HTMLElement, live: HTMLElement, vm: ValidityIconVm) => void {
  return mkFcValidityWriter(
    prefersReducedMotion, MOTION_LEAVE_FALLBACK_MS, 'hc-vicon',
    { ok: 'circle-check', bad: 'x' },
  );
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K, cls?: string, text?: string,
): HTMLElementTagNameMap[K] {
  const n = document.createElement(tag);
  if (cls) n.className = cls;
  if (text !== undefined) n.textContent = text;
  return n;
}

function fieldWrap(input: HTMLInputElement): {
  wrap: HTMLElement; vicon: HTMLElement; live: HTMLElement;
} {
  const wrap = el('span', 'hc-field-wrap');
  wrap.appendChild(input);
  const vicon = el('span', 'hc-vicon');
  wrap.appendChild(vicon);
  const live = el('span', 'hc-vlive hc-visually-hidden');
  live.setAttribute('aria-live', 'polite');
  return { wrap, vicon, live };
}

/** Build the create dialog on `host`. Exported for the suite; product code
 * enters through openCreateWired (which also mounts the browse seats). */
export function openCreateDialog(host: HTMLElement, opts: CreateDialogOpts): void {
  const debounceMs = opts.debounceMs ?? CREATE_PROBE_DEBOUNCE_MS;
  openDialog(host, (body, close) => {
    body.appendChild(el('h2', 'hc-dialog-title', 'Create release'));
    const form = el('form', 'hc-create-form hc-create-fixed');
    body.appendChild(form);

    // --- Name ----------------------------------------------------------
    const nameLabel = el('label', '', 'Name');
    const nameInput = el('input');
    nameInput.setAttribute('name', 'name');
    nameInput.setAttribute('data-autofocus', '');
    nameInput.setAttribute('required', '');
    nameInput.setAttribute('autocomplete', 'off');
    const nameSeat = fieldWrap(nameInput);
    nameLabel.appendChild(nameSeat.wrap);
    nameLabel.appendChild(nameSeat.live);
    form.appendChild(nameLabel);

    // --- Source branch (cut base) + typeahead overlay -------------------
    const branchLabel = el('label', '', 'Source branch');
    branchLabel.appendChild(el('span', 'hc-create-sub', ' (cut base)'));
    const branchRow = el('span', 'hc-create-branchrow');
    const branchInput = el('input');
    branchInput.setAttribute('name', 'source_branch');
    branchInput.setAttribute('autocomplete', 'off');
    branchInput.setAttribute('spellcheck', 'false');
    branchInput.setAttribute('aria-autocomplete', 'list');
    branchInput.setAttribute('aria-controls', 'hcTaPop');
    branchInput.setAttribute('aria-expanded', 'false');
    const branchSeat = fieldWrap(branchInput);
    branchRow.appendChild(branchSeat.wrap);
    const browseMount = el('span');
    browseMount.id = 'hcBranchBrowseMount';
    branchRow.appendChild(browseMount);
    branchLabel.appendChild(branchRow);
    branchLabel.appendChild(branchSeat.live);
    form.appendChild(branchLabel);

    // Server-refusal candidates only (the typeahead owns while-typing
    // suggestions); fixed-height reservation either way.
    const candsEl = el('div', 'hc-create-cands');
    candsEl.setAttribute('role', 'group');
    candsEl.setAttribute('aria-label', 'branch suggestions');
    form.appendChild(candsEl);
    const hint = el('p', 'hc-create-hint', CREATE_BRANCH_HINT);
    form.appendChild(hint);

    // --- Source SHA ------------------------------------------------------
    // Mirrors the branch field (owner batch, seam:create-modal-fields): the
    // field is full-width with its Browse-SHAs icon button INLINE to the
    // right (#hcShaBrowseMount, decorated by mountCreateBrowse), the green
    // check sits INSIDE the field (.hc-field-wrap), and the R57 corpus line
    // moved to its own line below (#hcShaTimelineMount).
    const shaLabel = el('label', '', 'Source SHA');
    shaLabel.appendChild(el('span', 'hc-create-sub', ' (optional)'));
    const shaRow = el('span', 'hc-create-sharow');
    const shaInput = el('input');
    shaInput.setAttribute('name', 'source_sha');
    shaInput.setAttribute('placeholder', SHA_LATEST_PLACEHOLDER);
    shaInput.setAttribute('autocomplete', 'off');
    shaInput.setAttribute('spellcheck', 'false');
    const shaSeat = fieldWrap(shaInput);
    shaRow.appendChild(shaSeat.wrap);
    const shaBrowseMount = el('span');
    shaBrowseMount.id = 'hcShaBrowseMount';
    shaRow.appendChild(shaBrowseMount);
    shaLabel.appendChild(shaRow);
    shaLabel.appendChild(shaSeat.live);
    form.appendChild(shaLabel);
    // The corpus-answer line's own line, below the SHA field row.
    const shaMount = el('div');
    shaMount.id = 'hcShaTimelineMount';
    form.appendChild(shaMount);

    // --- Configuration template (always in flow; visibility machine, D4) --
    const tplRow = el('label', 'hc-create-tplrow', 'Configuration template');
    tplRow.id = 'hcTplRow';
    tplRow.appendChild(el('span', 'hc-create-sub', " (seeds the Helm's settings)"));
    const tplMount = el('div', 'hc-tpl-dd');
    tplRow.appendChild(tplMount);
    const tplHidden = el('input');
    tplHidden.setAttribute('type', 'hidden');
    tplHidden.setAttribute('name', 'config_template_id');
    tplRow.appendChild(tplHidden);
    form.appendChild(tplRow);

    // --- Convert AISDLC-2.0 Corpus (#4249, P5 of the plan for #4194) -----
    // ADDITIVE, and the whole phase is judged on that: OFF is the default,
    // OFF reads nothing, OFF renders no rows, and OFF adds no key to the
    // POST body (convertRequestBody returns {} — see convert-corpus.ts).
    // The row itself is in flow from build like every other seat (D4), and
    // the list grows only on a USER action, never when an async seat lands.
    // Visibility is a CLASS, never [hidden]: home.test.mjs's fixed-size
    // probe walks this form and refuses any [hidden] in the flow.
    // The binary itself is the TOGGLE primitive (mkFcToggle, role=switch) —
    // the primitive-catalog ruling: everything that is not a grid bulk-select
    // is a toggle. The epic ROWS below are the carve-out that ruling names,
    // and they are the one shape it names: a multi-row select list.
    const convertRow = el('div', 'hc-create-convertrow');
    convertRow.id = 'hcConvertRow';
    const convertToggle = mkFcToggle({
      id: 'hcConvertToggle', label: CONVERT_TOGGLE_LABEL, text: '', checked: false,
      cls: 'hc-convert-toggle',
    });
    convertRow.appendChild(convertToggle.el);
    convertRow.appendChild(el('span', 'hc-convert-label', CONVERT_TOGGLE_LABEL));
    convertRow.appendChild(el('span', 'hc-create-sub', ' (optional)'));
    form.appendChild(convertRow);
    const convertHint = el('p', 'hc-create-hint hc-convert-hint', CONVERT_TOGGLE_HINT);
    convertHint.id = 'hcConvertHint';
    form.appendChild(convertHint);
    const convertMsg = el('p', 'hc-convert-msg');
    convertMsg.id = 'hcConvertMsg';
    convertMsg.setAttribute('aria-live', 'polite');
    form.appendChild(convertMsg);
    // #4246 P5, the owner 2026-09-18 17:45Z: "when checked, a Configure
    // button beside it opens a modal listing the detected epics in the
    // branch, each import/skip". The per-epic list USED to be an inline
    // group right here — `#hcConvertEpics`, role=group, aria-label
    // 'epics to convert' — and it MOVED, group id and accessible name
    // intact, into convert-configure-modal.ts. What stays in the form is
    // the button that opens it and a summary of what it holds, so the
    // create form's fixed box (D4) no longer grows by a row per epic.
    const convertConfigureBtn = mkIconBtn({
      glyph: 'gear', label: CONFIGURE_BTN_LABEL, size: 'sm',
      id: 'hcConvertConfigure', cls: 'hc-convert-configure-btn',
      state: 'disabled',
    });
    convertRow.appendChild(convertConfigureBtn);
    const convertSummary = el('span', 'hc-create-sub hc-convert-summary');
    convertSummary.id = 'hcConvertSummary';
    convertRow.appendChild(convertSummary);

    // --- The launch preflight (owner batch 2026-09-06): the SAME body the
    // POST sends, dry-run (?preflight=1) whenever a field settles; FAIL
    // blocks Create, WARN needs the "launch anyway" tick, and the
    // acknowledgement rides the POST as preflight_ack (recorded in audit).
    const preflight = mountPreflight(document, { verb: 'Create release', onChange: () => renderAll() });
    form.appendChild(preflight.el);

    const msg = el('p', 'hc-create-msg');
    msg.setAttribute('aria-live', 'polite');
    form.appendChild(msg);

    const actions = el('div', 'hc-dialog-actions');
    // Styles × Themes PR-2: these were already icon-only by hand (hc-btn-icon
    // + a hand-written aria-label/data-tip pair). They ride the primitive
    // now, which binds the two halves of the name from ONE field and paints
    // the additive ramp hc-btn-primary used to.
    const cancelBtn = mkIconBtn({ glyph: 'x', label: 'Cancel', attrs: { 'data-act': 'no' } });
    const submitBtn = mkIconBtn({
      glyph: 'plus', label: 'Create release', intent: 'additive',
      ctx: 'create-release-dialog', type: 'submit',
      state: 'disabled', // item 7: green-gated
      attrs: { 'data-act': 'yes' },
    });
    actions.appendChild(cancelBtn);
    actions.appendChild(submitBtn);
    form.appendChild(actions);
    // ----- state ---------------------------------------------------------
    const setVicon = makeViconWriter();
    let branchNames: string[] | null = null;
    let nameProbe: NameProbe = { state: 'idle' };
    let shaProbe: ShaProbe = { state: 'idle' };
    let nameSeq = 0;
    let shaSeq = 0;
    let dirty = false; // a typed edit wins over a late-arriving prefill
    let taVm: TypeaheadVm = { options: [], total: 0, overflow: 0 };
    // Convert-toggle state. #4246 moved the read off the toggle and onto
    // BRANCH SELECTION: `convertFetch` is the gate's evidence, so it is
    // populated whether or not the toggle is on, and turning the toggle off
    // does NOT discard it (a discarded probe would re-disable the checkbox
    // the moment the user untocked it). The read is still debounced and
    // `convertSeq`-guarded, and it still contributes NOTHING to the POST
    // body — `convertPanelVm` returns the OFF vm while `convertOn` is false,
    // so `convertRequestBody` sees state 'off' and adds no key.
    let convertOn = false;
    let convertFetch: ConvertFetch = { state: 'idle' };
    let convertUnchecked = new Set<string>();
    let convertSeq = 0;
    let convertFetchedRef: string | null = null;
    let convertTimer: ReturnType<typeof setTimeout> | null = null;
    // #4246 P5 — the Configure modal's handle and whether it is open. `null`
    // is not the same answer as "closed": the handle survives the exit
    // animation, so the BOOLEAN is what the restore guard reads.
    let configureHandle: { close(): void } | null = null;
    let configureOpen = false;
    const convertVm = (): ConvertPanelVm => convertPanelVm({
      enabled: convertOn,
      ref: branchInput.value.trim(),
      fetch: convertFetch,
      unchecked: [...convertUnchecked],
      // #4246: ONE mapping, in the module the suite drives — the dialog never
      // re-derives the probe from the fetch lane, so there is no second copy
      // of the rule to drift.
      probe: probeFromFetch(convertFetch),
    });

    const vms = (): { name: ValidityIconVm; branch: ValidityIconVm; sha: ValidityIconVm } => ({
      name: nameFieldVm(nameInput.value, nameProbe),
      branch: branchIconVm(branchFieldVm(branchInput.value, branchNames)),
      sha: shaFieldVm(shaInput.value, shaProbe),
    });

    const renderAll = (): void => {
      const v = vms();
      const bvm = branchFieldVm(branchInput.value, branchNames);
      // add/remove, not toggle(force) — the stub harness's classList has no
      // toggle, and this module is contractually stub-drivable.
      if (bvm.invalid) branchInput.classList.add('hc-input-invalid');
      else branchInput.classList.remove('hc-input-invalid');
      branchInput.setAttribute('aria-invalid', bvm.invalid ? 'true' : 'false');
      setVicon(nameSeat.vicon, nameSeat.live, v.name);
      setVicon(branchSeat.vicon, branchSeat.live, v.branch);
      setVicon(shaSeat.vicon, shaSeat.live, v.sha);
      const gate = ackVm(preflight.vm(), preflight.acknowledged());
      const cvm = renderConvert();
      setBtnState(
        submitBtn,
        createSubmitOk(v.name, v.branch, v.sha) && gate.canSubmit && cvm.submitOk ? 'enabled' : 'disabled',
      );
    };

    /** Paint the convert panel and hand back the vm the gate reads. With the
     * toggle OFF this empties both seats and returns the OFF vm — no rows,
     * no message, submitOk true — so nothing here can reach the gate. */
    const renderConvert = (): ConvertPanelVm => {
      let cvm = convertVm();
      // #4246 — the branch changed underneath a checkbox that was already ON
      // and the probe ANSWERED that the new branch has no corpus. Switching it
      // off is the only honest outcome: leaving it ON would submit an upgrade
      // of a branch the console has measured as having nothing to upgrade.
      //
      // The predicate is `probeDisqualifies`, NOT `!checkboxEnabled`. Those
      // differ on exactly the transients — a branch edit passes through
      // 'loading' on every keystroke, so the weaker predicate would un-tick
      // the box the moment the user touched the branch field, and 'miss' is an
      // unknown the submit gate already refuses by name. Bounded: the vm is
      // recomputed EXACTLY ONCE here and `convertOn` is false by then, so this
      // cannot re-enter.
      if (convertOn && probeDisqualifies(probeFromFetch(convertFetch))) {
        convertOn = false;
        convertToggle.checked = false;
        convertUnchecked = new Set<string>();
        console.info(
          `convert-toggle: ON checkbox forced OFF on a branch change — ` +
          `ref ${branchInput.value.trim() || '(none)'}, probe ${probeFromFetch(convertFetch)}: ` +
          `${cvm.checkboxReason ?? 'no reason reported'}`,
        );
        cvm = convertVm();
      }
      // The gate is painted from the vm, never computed here — one rule, one
      // place, and the suite drives that place directly.
      convertToggle.disabled = !cvm.checkboxEnabled;
      convertToggle.el.setAttribute?.('title', cvm.checkboxReason ?? '');
      convertToggle.el.setAttribute?.('aria-disabled', cvm.checkboxEnabled ? 'false' : 'true');
      convertMsg.textContent = cvm.message ?? '';
      // #4246 P5 — the rows live in the modal now; the form paints the door
      // to them. The enable rule is `configureButtonVm`'s, not this file's:
      // one rule, one place, and the suite drives that place directly.
      const btnVm = configureButtonVm(cvm, convertOn);
      setBtnState(convertConfigureBtn, btnVm.enabled ? 'enabled' : 'disabled');
      convertConfigureBtn.setAttribute?.('title', btnVm.reason ?? '');
      convertConfigureBtn.setAttribute?.('aria-disabled', btnVm.enabled ? 'false' : 'true');
      convertSummary.textContent = configureSummary(cvm);
      // The panel stopped offering a list underneath an OPEN modal (the
      // branch changed, the toggle was forced off). Close it rather than
      // leave rows on screen that no longer describe anything — and drop the
      // restore first, because the set it would restore belongs to a branch
      // that is no longer selected.
      if (!btnVm.enabled && configureOpen) {
        configureOpen = false;
        const h = configureHandle;
        configureHandle = null;
        h?.close();
      }
      return cvm;
    };

    /** Open the import/skip modal on the stacked overlay above this dialog.
     * The create form stays mounted underneath — that is what `stackHostFor`
     * inside the modal module buys, and why this is not a content swap.
     *
     * The modal mutates ONE thing: `convertUnchecked`. It adds no producer of
     * the POST body; `convertRequestBody` is still the only one, reading the
     * vm this set feeds. */
    const openConfigure = (): void => {
      if (configureOpen) return;
      const cvm = convertVm();
      if (!configureButtonVm(cvm, convertOn).enabled) return;
      configureOpen = true;
      const modalOpts: ConfigureModalOpts = {
        vm: convertVm,
        unchecked: [...convertUnchecked],
        onChange: (next) => {
          // Guarded: a restore fired by a close this dialog itself asked for
          // must not write back a selection for a branch that has changed.
          if (!configureOpen) return;
          convertUnchecked = new Set(next);
          renderAll();
        },
        onClose: () => { configureOpen = false; configureHandle = null; },
      };
      configureHandle = openConfigureModal(host, modalOpts);
    };
    convertConfigureBtn.addEventListener('click', openConfigure);

    /** Read both halves for the current cut base. #4246: called on BRANCH
     * SELECTION, toggle or no toggle — it is what decides whether the toggle
     * may be ticked at all, so gating it on the toggle would be circular. */
    const loadConvertEpics = (): void => {
      const ref = branchInput.value.trim();
      if (!ref) return;
      if (convertFetchedRef === ref && convertFetch.state === 'done') return;
      const my = ++convertSeq;
      convertFetch = { state: 'loading' };
      renderAll();
      void (async () => {
        const r = await jsonFetch<ConvertEpicsDto & { error?: string }>(
          'GET', `/api/vcs/convert-epics?branch=${encodeURIComponent(ref)}`);
        if (my !== convertSeq) return; // a later read supersedes this answer
        if (r.status === 200 && r.data && r.data.discovery && r.data.taught) {
          convertFetchedRef = ref;
          convertFetch = { state: 'done', payload: r.data };
        } else {
          convertFetchedRef = null;
          convertFetch = { state: 'error', detail: r.data?.error ?? `status ${r.status}` };
        }
        renderAll();
      })();
    };
    const scheduleConvertLoad = (): void => {
      if (convertTimer) clearTimeout(convertTimer);
      convertTimer = setTimeout(loadConvertEpics, debounceMs);
    };
    convertToggle.addEventListener('change', () => {
      convertOn = convertToggle.checked === true;
      if (!convertOn) {
        // Back to the identity path — but the PROBE survives. Under #4249
        // this reset `convertFetch` to 'idle', which #4246 cannot do: the
        // probe is the gate's only evidence, and discarding it would disable
        // the checkbox the instant the user unticked it, on a branch already
        // measured to have a corpus. The identity claim does not depend on
        // this reset: `convertPanelVm` keys the POST body on `enabled`, and
        // the existing arm 1c pins that a FULLY LOADED payload with the
        // toggle off still contributes 0 keys.
        convertUnchecked = new Set<string>();
      }
      renderAll();
      // #4246: no second read on toggle-on — the branch-selection probe's
      // payload is reused (`convertFetchedRef === ref && state === 'done'`
      // short-circuits), so ticking the box costs nothing.
      if (convertOn) loadConvertEpics();
    });

    // ONE body composer: the dry-run judges exactly the object the POST
    // sends (a preflight over a different body certifies nothing).
    const createBody = (): Record<string, unknown> => {
      const body: Record<string, unknown> = {
        name: nameInput.value.trim(),
        source_branch: branchInput.value.trim() || null,
        source_sha: shaInput.value.trim() || null,
        config_template_id: tplHidden.value.trim() || null,
      };
      // {} while the toggle is OFF — so this line adds NO KEY and the body
      // stays byte-identical to the pre-#4249 body. A `convert_corpus: null`
      // would pass every behavioural test and still change what the server
      // receives; create-flow-convert-toggle.test.mjs pins the difference
      // against the PINNED pre-#4249 dialog.
      return { ...body, ...convertRequestBody(convertVm()) };
    };
    let preflightSeq = 0;
    let preflightTimer: ReturnType<typeof setTimeout> | null = null;
    const runPreflight = (): void => {
      const v = vms();
      if (!createSubmitOk(v.name, v.branch, v.sha)) return; // nothing to certify yet
      const seq = ++preflightSeq;
      preflight.set(null, 'running');
      void (async () => {
        const r = await jsonFetch<PreflightDto & { error?: string; refusal?: string }>(
          'POST', '/api/releases?preflight=1', createBody());
        if (seq !== preflightSeq) return; // a later run supersedes this answer
        if (r.status === 200 && r.data && Array.isArray(r.data.checks)) preflight.set(r.data);
        else if (r.status === 200) preflight.set(null, 'idle', r.data?.error ?? 'No template snapshot to preflight.');
        else preflight.set(null, 'error', r.data?.error ? `Preflight unavailable: ${r.data.error}` : undefined);
        renderAll();
      })();
    };
    const schedulePreflight = (): void => {
      if (preflightTimer) clearTimeout(preflightTimer);
      preflightTimer = setTimeout(runPreflight, 450);
    };
    form.addEventListener('input', schedulePreflight);

    // Server-refusal candidates (the 400/409 lane) — one-click fills.
    const renderCandidates = (cands: string[]): void => {
      candsEl.replaceChildren();
      cands.forEach((c, i) => {
        const chip = el('button', 'hc-cand', c);
        chip.setAttribute('type', 'button');
        // DEFERRED to PR-3c, not converted: a suggestion CHIP whose whole
        // face is the candidate branch name — icon-only would delete the
        // very text you are choosing between.
        chip.dataset.btnRole = 'chip';
        (chip as HTMLElement).style.animationDelay = `${i * 30}ms`;
        chip.addEventListener('click', () => {
          branchInput.value = c;
          dirty = true;
          renderAll();
          renderTypeahead(false);
          branchInput.focus?.();
        });
        candsEl.appendChild(chip);
      });
    };

    // ----- typeahead (D6) -------------------------------------------------
    // PR-3a: the list machinery moved to the shared primitive
    // (attachFcTypeahead) — this was the product's only combobox and it was
    // welded into this one dialog, so nothing else could have it. What stays
    // here is what is genuinely this dialog's: WHAT matches (typeaheadOptions
    // over the modal-open branch cache) and what an accept means. The `hc-ta-*`
    // class names stay as layout hooks so the fixed-size probe and the
    // suggestion assertions in home.test.mjs keep addressing the same nodes.
    const ta = attachFcTypeahead(branchInput, branchSeat.wrap, {
      label: 'branch',
      popId: 'hcTaPop',
      skin: {
        pop: 'hc-ta-pop', item: 'hc-ta-item', label: 'hc-ta-label',
        hint: 'hc-ta-count', active: 'hc-ta-active', more: 'hc-ta-more',
        drill: 'hc-ta-dir',
      },
      suggest: (q) => {
        if (!branchNames) return { options: [], total: 0, overflow: 0 };
        taVm = typeaheadOptions(q, branchNames);
        return {
          total: taVm.total,
          overflow: taVm.overflow,
          options: taVm.options.map((o) => ({
            value: o.value,
            label: o.label,
            glyph: o.kind === 'dir' ? 'folder-open' : 'git-branch',
            hint: o.kind === 'dir' ? `(${o.leafCount})` : undefined,
            drill: o.kind === 'dir',
          })),
        };
      },
      overflowText: (vm) => `+${vm.overflow} more (${vm.total} total) — keep typing`,
      onAccept: () => {
        dirty = true;
        renderAll();
      },
    });
    const renderTypeahead = (keepOpen: boolean): void => ta.render(keepOpen);

    // ----- input lanes ----------------------------------------------------
    branchInput.addEventListener('input', () => {
      dirty = true;
      // A new cut base is a new epic population. Debounced like every other
      // probe in this dialog — never a read per keystroke — and a no-op
      // while the toggle is off.
      convertFetchedRef = null;
      renderAll();
      renderTypeahead(false);
      scheduleConvertLoad();
    });

    nameInput.addEventListener('input', () => {
      const v = nameInput.value.trim();
      const my = ++nameSeq;
      nameProbe = { state: 'idle' };
      renderAll();
      if (!v || !clientNameGate(v).ok) return; // local verdict already final
      setTimeout(() => {
        if (my !== nameSeq) return; // superseded while debouncing
        nameProbe = { state: 'pending' };
        renderAll();
        void (async () => {
          const r = await jsonFetch<NameAvailablePayload>(
            'GET', `/api/releases/name-available?name=${encodeURIComponent(v)}`);
          if (my !== nameSeq) return; // stale answer — drop
          if (r.status === 200 && r.data && typeof r.data.available === 'boolean') {
            nameProbe = r.data.available
              ? { state: 'free', measured: r.data.measured ?? '(measured line missing)' }
              : {
                  state: 'taken',
                  reason: r.data.reason ?? `release name '${v}' already exists`,
                  measured: r.data.measured ?? '',
                };
          } else {
            nameProbe = { state: 'error', detail: `status ${r.status}` };
          }
          renderAll();
        })();
      }, debounceMs);
    });

    shaInput.addEventListener('input', () => {
      const v = shaInput.value.trim();
      const my = ++shaSeq;
      shaProbe = { state: 'idle' };
      renderAll();
      if (!v || !/^[0-9a-f]{4,40}$/i.test(v)) return; // local verdict final
      setTimeout(() => {
        if (my !== shaSeq) return;
        shaProbe = { state: 'pending' };
        renderAll();
        void (async () => {
          const r = await jsonFetch<CorpusBasePayload>(
            'GET', `/api/vcs/corpus-base?sha=${encodeURIComponent(v)}`);
          if (my !== shaSeq) return;
          if (r.status === 200 && r.data?.measured) {
            shaProbe = { state: 'resolved', measured: r.data.measured };
          } else if (r.status === 404 || r.status === 400) {
            shaProbe = {
              state: 'unresolved',
              detail: r.data?.error ?? `sha does not resolve (${r.status})`,
            };
          } else {
            shaProbe = { state: 'error', detail: `status ${r.status}` };
          }
          renderAll();
        })();
      }, debounceMs);
    });

    // ----- seat reads (all parallel; each degrades by name) ---------------
    void (async () => {
      const cc = await jsonFetch<CreateSeatConfig>('GET', '/api/client-config');
      const pf = prefillVm(cc.status === 200 ? cc.data : null);
      branchInput.dataset.tip = pf.title;
      if (pf.placeholder) branchInput.setAttribute('placeholder', pf.placeholder);
      if (!dirty && pf.value) {
        branchInput.value = pf.value;
        renderAll();
        // #4246: a PREFILLED cut base is a SELECTED cut base. Without this the
        // gate would stay 'idle' — and therefore disabled — for every user who
        // accepts the default branch, which is most of them, and the checkbox
        // would look broken rather than gated.
        scheduleConvertLoad();
      }
    })();
    void (async () => {
      const t0 = Date.now();
      const r = await jsonFetch<{ names?: string[]; measured?: string }>(
        'GET', '/api/vcs/branch-names');
      const ms = Date.now() - t0;
      branchNames = r.status === 200 && Array.isArray(r.data?.names) ? r.data!.names! : null;
      if (branchNames) {
        // D6's measured boot line — count + ms + the server's own sentence.
        console.info(typeaheadBootLine(branchNames.length, ms, r.data?.measured));
      }
      renderAll();
    })();
    void (async () => {
      const r = await jsonFetch<{ id: string; name: string; is_default: boolean }[]>(
        'GET', '/api/config-templates');
      if (r.status !== 200 || !Array.isArray(r.data) || r.data.length === 0) return;
      const pick = templatePickerVm(r.data);
      if (!pick.selectedId) return;
      tplHidden.value = pick.selectedId;
      const ctl: DropdownCtl = mkDropdown({
        id: 'hcTplPick',
        options: () => pick.options.map((o) => ({ value: o.id, label: o.label })),
        value: () => tplHidden.value || pick.selectedId!,
        onPick: (v) => { tplHidden.value = v; ctl.set(); schedulePreflight(); },
      });
      tplMount.appendChild(ctl.el);
      ctl.set();
      // Visibility, never [hidden]: the row's space was reserved from build
      // (D4 — the modal must not change size when the picker arrives).
      tplRow.classList.add('hc-tpl-ready');
      schedulePreflight();
    })();

    cancelBtn.addEventListener('click', close);
    form.addEventListener('submit', (ev: Event) => {
      ev.preventDefault?.();
      const v = vms();
      if (!createSubmitOk(v.name, v.branch, v.sha)) {
        // The gate names the first non-green field — the button being
        // disabled makes this belt-and-braces for synthetic submits.
        const firstBad = !v.name.green ? v.name : !v.branch.green ? v.branch : v.sha;
        showDialogMsg(msg, firstBad.tip ?? BRANCH_SYNTAX_REASON);
        return;
      }
      // The busy face is the primitive's derivation (hc-btn-busy was the
      // .hc-btn spinner and paints nothing on an .ibtn).
      const gate = ackVm(preflight.vm(), preflight.acknowledged());
      if (!gate.canSubmit) {
        showDialogMsg(msg, gate.reason ?? 'Preflight refused.');
        return;
      }
      // Toggle ON with nothing convertible is a request the console cannot
      // honour; it refuses BY NAME rather than creating a release that
      // silently converts nothing. OFF can never reach this branch.
      const cvm = convertVm();
      if (!cvm.submitOk) {
        showDialogMsg(msg, cvm.submitReason ?? 'The epic list is not ready.');
        return;
      }
      setBtnState(submitBtn, 'busy');
      void (async () => {
        const { status, data } = await jsonFetch<{
          slug?: string; error?: string; candidates?: string[]; refusal?: string; preflight?: PreflightDto;
        }>('POST', '/api/releases', { ...createBody(), ...ackBody(preflight.vm(), preflight.acknowledged()) });
        setBtnState(submitBtn, 'enabled');
        // The server's own gate re-judged and refused: paint ITS result (the
        // client's run is advisory, never a certificate).
        if (status === 409 && (data?.refusal === 'preflight-failed' || data?.refusal === 'preflight-warn') && data?.preflight) {
          preflight.set(data.preflight);
          showDialogMsg(msg, data.error ?? 'preflight refused');
          renderAll();
          return;
        }
        const apex = opts.apex();
        if (status === 201 && data?.slug && apex) {
          // Land the user in the new release's subdomain — served at second
          // zero by the proxy's console lane (T1.7): no VM exists yet.
          location.href = `https://${data.slug}.${apex}/`;
          return;
        }
        if (status === 201) {
          close();
          await opts.onCreated();
          return;
        }
        // The authoritative gate refused: candidates land in the SAME
        // first-class invalid presentation the instant lane uses.
        if ((status === 400 || status === 409) && Array.isArray(data?.candidates) && data!.candidates!.length) {
          branchInput.classList.add('hc-input-invalid');
          branchInput.setAttribute('aria-invalid', 'true');
          renderCandidates(data!.candidates!);
        }
        showDialogMsg(msg, data?.error ?? `refused (${status})`);
        renderAll();
      })();
    });

    renderAll(); // initial paint: sha shows its green 'Latest' check (item 6)
  }, { className: 'hc-create-dialog' });
}

/** The product entry: build the dialog, then decorate the two named browse
 * seats (#hcBranchBrowseMount / #hcShaTimelineMount). The browse modals
 * open STACKED above this form (D7) — the form never unmounts, so there
 * is no restore lane any more. */
export function openCreateWired(host: HTMLElement, opts: CreateDialogOpts): void {
  openCreateDialog(host, opts);
  // Prefetch the FULL branch listing the instant the modal opens — parallel
  // to openCreateDialog's /api/vcs/branch-names typeahead prefetch — so
  // Browse renders from cache instantly (owner: "cache them as soon as the
  // create modal opens, before the user clicks browse"). A failure or a
  // still-in-flight prefetch leaves branchPrefetch null and mountCreateBrowse
  // falls back to the on-click fetch — no regression, no double-render.
  let branchPrefetch: BranchFetchResult | null = null;
  void (async () => {
    const r = await fetchAllBranches();
    if (r.ok) branchPrefetch = r;
  })();
  mountCreateBrowse({ panel: host, host, getPreloaded: () => branchPrefetch });
}
